// Haptic feedback for combat impact moments.
//
// Surface: navigator.vibrate — works on Android browsers (Chrome,
// Samsung, Firefox) and PWAs and Capacitor 7 Android. iOS Safari does
// not support the API; iOS Capacitor builds can later swap in
// `@capacitor/haptics` for ImpactFeedbackGenerator-style cues.
//
// Throttling — combat fires dozens of hits per fight; vibrating on
// every one creates wrist fatigue (the dominant beta complaint along
// with audio cacophony). MIN_GAP_MS gates back-to-back fires so the
// wrist gets a beat to settle between buzzes.

import { useAccountStore } from '../stores/account';

const MIN_GAP_MS = 60;
let lastFireAt = 0;

type Level = 'light' | 'medium' | 'heavy';

// Patterns in milliseconds. Single-pulse for light/medium, double for
// heavy so the body feels weighty rather than just longer.
const PATTERN_BY_LEVEL: Record<Level, number | number[]> = {
  light: 8,
  medium: 18,
  heavy: [28, 18, 28],
};

function isVibrateAvailable(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
}

function hapticsAllowed(): boolean {
  if (!isVibrateAvailable()) return false;
  // Piggyback on the existing audio-SFX toggle for now — players who mute
  // SFX almost certainly also want silent buzzes. A dedicated toggle can
  // land later if the use cases diverge.
  const { audioMaster, audioSfx } = useAccountStore.getState();
  if (audioMaster <= 0 || audioSfx <= 0) return false;
  return true;
}

/** Tap-level buzz — UI-tier feedback. Use sparingly outside combat. */
export function tap(): void {
  fire('light');
}

/**
 * Impact-level buzz scaled to a combat damage amount. Larger hits get a
 * weightier pulse. amount is the raw damage value; we map it to a level.
 */
export function impact(amount: number): void {
  const level: Level = amount >= 8 ? 'heavy' : amount >= 3 ? 'medium' : 'light';
  fire(level);
}

/** End-of-round signal — winning or losing the fight. Always heavy. */
export function finale(): void {
  // Bypass MIN_GAP_MS — final cue should always land even if a hit
  // happened on the same frame.
  if (!hapticsAllowed()) return;
  navigator.vibrate(PATTERN_BY_LEVEL.heavy);
  lastFireAt = performance.now();
}

function fire(level: Level): void {
  if (!hapticsAllowed()) return;
  const now = performance.now();
  if (now - lastFireAt < MIN_GAP_MS) return;
  navigator.vibrate(PATTERN_BY_LEVEL[level]);
  lastFireAt = now;
}

export const haptics = { tap, impact, finale };
